import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Tag,
    Modal,
    Form,
    Select,
    message,
    Card,
    Popconfirm,
    Tooltip,
    Typography,
    Row,
    Col,
    Statistic,
    Empty
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    LinkOutlined,
    DisconnectOutlined,
    SyncOutlined,
    GlobalOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import evmService from '../../../shared/services/evmService';
import { userPath } from '../../../shared/config/paths';
import { formatDateTime } from '../../../shared/utils/date';

// 后端 EIP.status 与绑定关系可能不一致（已挂 vm_id 但 status 仍为 available）：以 vm_id 为准推导展示状态
const effectiveStatus = (eip) => {
    if (eip.status === 'available' && eip.vm_id) return 'bound';
    return eip.status;
};

const { Option } = Select;
const { Text } = Typography;

const MyEip = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [eips, setEips] = useState([]);
    const [loading, setLoading] = useState(false);
    const [allocateModalVisible, setAllocateModalVisible] = useState(false);
    const [bindModalVisible, setBindModalVisible] = useState(false);
    const [currentEip, setCurrentEip] = useState(null);
    const [bindableVms, setBindableVms] = useState([]);
    const [bindableVmsLoading, setBindableVmsLoading] = useState(false);
    const [stats, setStats] = useState({ total: 0, allocated: 0, bound: 0 });
    const [myVms, setMyVms] = useState([]);
    const [myVmsLoading, setMyVmsLoading] = useState(false);
    const [allocating, setAllocating] = useState(false);
    const [binding, setBinding] = useState(false);

    const [allocateForm] = Form.useForm();
    const [bindForm] = Form.useForm();

    useEffect(() => {
        loadEips();
    }, []);

    const loadEips = async () => {
        setLoading(true);
        try {
            const response = await evmService.getEipList();
            if (response.status === 'success') {
                const eipList = response.data?.eips || [];
                setEips(eipList);
                const newStats = {
                    total: eipList.length,
                    allocated: eipList.filter(e => effectiveStatus(e) === 'available').length,
                    bound: eipList.filter(e => effectiveStatus(e) === 'bound').length
                };
                setStats(newStats);
            } else {
                message.error(response.msg || t('Failed to load Elastic IPs'));
            }
        } catch (error) {
            message.error(t('Failed to load Elastic IPs'));
        } finally {
            setLoading(false);
        }
    };

    // 弹性 IP 只能在自己的云服务器上购买（按该实例订阅计费）
    const loadMyVms = async () => {
        setMyVmsLoading(true);
        try {
            const response = await evmService.getVmList({ page: 1, limit: 500 });
            if (response.status === 'success') {
                const d = response.data;
                const list = Array.isArray(d) ? d : (d?.items || []);
                setMyVms(list.filter((vm) => vm.status === 'active'));
            }
        } catch (error) {
            console.error('Failed to load VMs', error);
        } finally {
            setMyVmsLoading(false);
        }
    };

    const loadBindableVms = async (eipId) => {
        setBindableVmsLoading(true);
        try {
            const response = await evmService.getBindableVms(eipId);
            if (response.status === 'success') {
                setBindableVms(response.data?.vms || []);
            }
        } catch (error) {
            console.error('Failed to load bindable VMs', error);
        } finally {
            setBindableVmsLoading(false);
        }
    };

    const showAllocateModal = () => {
        allocateForm.resetFields();
        loadMyVms();
        setAllocateModalVisible(true);
    };

    const showBindModal = async (eip) => {
        setCurrentEip(eip);
        bindForm.resetFields();
        await loadBindableVms(eip.id);
        setBindModalVisible(true);
    };

    const handleAllocate = async (values) => {
        setAllocating(true);
        try {
            const response = await evmService.allocateEip(values);
            if (response.status === 'success') {
                const d = response.data || {};
                setAllocateModalVisible(false);
                if (d.invoice_id && !d.paid) {
                    // Priced IPv4: invoice created but balance didn't cover it.
                    message.info(t('Allocation will occur automatically after you pay the invoice'));
                    navigate(userPath(`/billing/invoices/${d.invoice_id}`));
                    return;
                }
                message.success(d.invoice_id ? t('Paid, allocating IP') : t('Elastic IP allocated'));
                loadEips();
            } else {
                message.error(response.msg || t('Failed to allocate Elastic IP'));
            }
        } catch (error) {
            message.error(error?.message || t('Failed to allocate Elastic IP'));
        } finally {
            setAllocating(false);
        }
    };

    const handleBind = async (values) => {
        setBinding(true);
        try {
            const response = await evmService.bindEip({
                eip_id: currentEip.id,
                vm_id: values.vm_id
            });
            if (response.status === 'success') {
                message.success(t('Elastic IP bound'));
                setBindModalVisible(false);
                loadEips();
            } else {
                message.error(response.msg || t('Failed to attach elastic IP'));
            }
        } catch (error) {
            message.error(error?.message || t('Failed to attach elastic IP'));
        } finally {
            setBinding(false);
        }
    };

    const handleUnbind = async (eip) => {
        try {
            const response = await evmService.unbindEip(eip.id);
            if (response.status === 'success') {
                message.success(t('Elastic IP unbound'));
                loadEips();
            } else {
                message.error(response.msg || t('Failed to unbind'));
            }
        } catch (error) {
            message.error(t('Failed to unbind'));
        }
    };

    const handleRelease = async (eip) => {
        try {
            const response = await evmService.releaseEip(eip.id);
            if (response.status === 'success') {
                message.success(t('Elastic IP released'));
                loadEips();
            } else {
                message.error(response.msg || t('Release failed'));
            }
        } catch (error) {
            message.error(t('Release failed'));
        }
    };

    const getTypeTag = (type) => {
        return type === 'v6' ? (
            <Tag color="purple">IPv6</Tag>
        ) : (
            <Tag color="blue">IPv4</Tag>
        );
    };

    const getStatusTag = (status) => {
        const statusMap = {
            'available': { color: 'orange', text: t('Not bound') },
            'binding': { color: 'processing', text: t('Binding') },
            'unbinding': { color: 'processing', text: t('Unbinding') },
            'bound': { color: 'green', text: t('Bound') },
            'binding_failed': { color: 'red', text: t('Binding failed') },
            'error': { color: 'red', text: t('Error') }
        };
        const config = statusMap[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 60
        },
        {
            title: t('IP address'),
            dataIndex: 'public_ip',
            key: 'public_ip',
            width: 200,
            render: (ip) => <Text copyable>{ip}</Text>
        },
        {
            title: t('Type'),
            dataIndex: 'ip_version',
            key: 'ip_version',
            width: 80,
            render: (type) => getTypeTag(type)
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (_, record) => getStatusTag(effectiveStatus(record))
        },
        {
            title: t('Attach instance'),
            dataIndex: 'vm_hostname',
            key: 'vm_hostname',
            width: 150,
            render: (hostname, record) => hostname || record.vm?.hostname || (record.vm_id ? `VM-${record.vm_id}` : '-')
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            width: 160,
            render: (v) => formatDateTime(v)
        },
        {
            title: t('Actions'),
            key: 'action',
            fixed: 'right',
            width: 150,
            render: (_, record) => (
                <Space size="small">
                    {effectiveStatus(record) === 'available' && (
                        <Tooltip title={t('Attach to instance')}>
                            <Button
                                size="small"
                                type="primary"
                                icon={<LinkOutlined />}
                                onClick={() => showBindModal(record)}
                            />
                        </Tooltip>
                    )}
                    {effectiveStatus(record) === 'bound' && (
                        <Popconfirm
                            title={t('Detach this elastic IP?')}
                            onConfirm={() => handleUnbind(record)}
                            okText={t('Unbind')}
                            cancelText={t('Cancel')}
                        >
                            <Tooltip title={t('Unbind')}>
                                <Button size="small" icon={<DisconnectOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    )}
                    {effectiveStatus(record) === 'available' && !record.vm_id && (
                        <Popconfirm
                            title={t('Release this elastic IP?')}
                            description={t('Once released, this IP returns to the address pool and cannot be recovered.')}
                            onConfirm={() => handleRelease(record)}
                            okText={t('Release')}
                            okButtonProps={{ danger: true }}
                            cancelText={t('Cancel')}
                        >
                            <Tooltip title={t('Release')}>
                                <Button size="small" danger icon={<DeleteOutlined />} />
                            </Tooltip>
                        </Popconfirm>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div>
            <Card
                title={<span><GlobalOutlined /> {t('Elastic IPs')}</span>}
                extra={
                    <Space>
                        <Button type="primary" icon={<PlusOutlined />} onClick={showAllocateModal}>
                            {t('Allocate elastic IP')}
                        </Button>
                        <Button icon={<SyncOutlined />} onClick={loadEips}>
                            {t('Refresh')}
                        </Button>
                    </Space>
                }
            >
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    <Col span={8}>
                        <Statistic title={t('All')} value={stats.total} />
                    </Col>
                    <Col span={8}>
                        <Statistic title={t('Not bound')} value={stats.allocated} valueStyle={{ color: '#faad14' }} />
                    </Col>
                    <Col span={8}>
                        <Statistic title={t('Bound')} value={stats.bound} valueStyle={{ color: '#52c41a' }} />
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={eips}
                    loading={loading}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 10 }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={t('No elastic IPs yet. Click "Allocate elastic IP" in the top right.')}
                            />
                        )
                    }}
                />
            </Card>

            {/* Allocate EIP Modal */}
            <Modal
                title={t('Allocate elastic IP')}
                open={allocateModalVisible}
                onCancel={() => setAllocateModalVisible(false)}
                onOk={() => allocateForm.submit()}
                confirmLoading={allocating}
            >
                <Form form={allocateForm} layout="vertical" onFinish={handleAllocate}>
                    <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>{t('IPv4 is billed monthly and deducted from your balance')}</Text>
                    <Form.Item
                        name="vm_id"
                        label={t('Cloud servers')}
                        rules={[{ required: true, message: t('Please select a cloud server') }]}
                    >
                        <Select
                            placeholder={t('Select a cloud server')}
                            loading={myVmsLoading}
                            showSearch
                            optionFilterProp="label"
                            notFoundContent={myVmsLoading ? null : t('No running cloud servers')}
                            options={myVms.map((vm) => ({
                                value: vm.id,
                                label: `${vm.hostname || `VM-${vm.id}`}${vm.ip_address ? ` (${vm.ip_address})` : ''}`,
                            }))}
                        />
                    </Form.Item>
                    {/* Elastic IPs are IPv4 only; IPv6 is a /64 per instance, bought on the instance. */}
                    <Text type="secondary" style={{ display: 'block' }}>{t("Add IPv6 from the instance's \"Upgrade/Scale\" tab")}</Text>
                </Form>
            </Modal>

            {/* Bind EIP Modal */}
            <Modal
                title={t('Attach elastic IP: {{value}}', { value: currentEip?.public_ip || '' })}
                open={bindModalVisible}
                onCancel={() => setBindModalVisible(false)}
                onOk={() => bindForm.submit()}
                confirmLoading={binding}
            >
                <Form form={bindForm} layout="vertical" onFinish={handleBind}>
                    <Form.Item
                        name="vm_id"
                        label={t('Select an instance')}
                        rules={[{ required: true, message: t('Select an instance') }]}
                    >
                        <Select
                            placeholder={t('Select the instance to bind')}
                            loading={bindableVmsLoading}
                            showSearch
                            optionFilterProp="children"
                        >
                            {bindableVms.map(vm => (
                                <Option key={vm.id} value={vm.id}>
                                    {vm.hostname || `VM-${vm.id}`} - {vm.private_ip || t('No private IP')}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default MyEip;
